import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateTimeOffQueries } from '@/modules/common/utils/query-invalidation';

import { reviewTimeOffRequest } from '../api/time-offs.api';
import { ReviewTimeOffRequestParams } from '../types/dto/time-off-review.dto';
import { TimeOffReviewDecision } from '../types/time-off-review';

interface UseReviewTimeOffRequestParams {
  onSuccess?: () => void;
}

/**
 * Approve/decline mutation for the time-off approvals table. On success it
 * invalidates the time-off query root (covers the to-review list) and shows a
 * decision-specific success toast.
 */
export const useReviewTimeOffRequest = ({ onSuccess }: UseReviewTimeOffRequestParams = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.timeOff');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (params: ReviewTimeOffRequestParams) => reviewTimeOffRequest(params),
    onSuccess: async (_data, variables) => {
      await invalidateTimeOffQueries(queryClient);
      displayToastSuccess(
        variables.decision === TimeOffReviewDecision.Approve
          ? t('approvals.toasts.approveSuccess')
          : t('approvals.toasts.declineSuccess')
      );
      onSuccess?.();
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'approvals.toasts.error');
    },
  });
};
